/* eslint-disable @typescript-eslint/member-ordering */
import { action, computed, observable, runInAction } from 'mobx';
import { GlobalAdministrationService } from '../service/GlobalAdministrationService';
import { ErrorStore } from '../../../modules/common/stores';
import type { UserModel } from '../types/UserModel';
import { RoleModel } from '../types/RoleModel';
import { message } from 'antd';
import { UserProfileStore } from '../../../modules/common/stores';
import { SearchUsersAutoCompleteSourceItem } from '../../common/types';
import security from '../../common/services/SecurityService';
import { UsersGroup } from '../types/GroupModel';
import permissions from '../../authorization/Permissions';
import { CustomRole } from '../types';
import { ResponseData } from '../../common/services/AppClient';
import { GroupRoleMapping } from '../types/GroupRoleMapping';
import { UsersStore } from '../../../modules/common/stores';
import _ from 'lodash';
import { Utils } from '../../common/services/Utils';
import { ProjectsService } from '../../../modules/common/services';
import { ProjectResult } from '../../common/types';
import {
    BulkUserBatch,
    BulkUserImportRequest,
    BulkUserImportResponse,
    BulkUserRow,
    BulkUserValidationResult
} from '../types/BulkUserModels';

export enum LoginTypes {
    internalUser = 'Internal User',
    domainUser = 'Domain User'
}

type Group = {
    users: UserModel[];
    usersGroup: UsersGroup;
};

export default class UsersManagerStore {
    usersPageSize: number = 20;

    @observable
    usersTotalCount: number = 0;

    @observable
    usersCurrentPage = 1;

    @observable
    isLoading: boolean = false;

    @observable
    showLoadMoreFooter: boolean = true;

    @observable
    users: UserModel[] = [];

    @observable
    selectedUser: UserModel | null;

    @observable
    selectedProjectId: string | null;

    @observable
    roles: RoleModel[];

    @observable
    groupRoleMappings: GroupRoleMapping[];

    @observable
    isAddUserDialogVisible: boolean;

    @observable
    isEditUserDialogVisible: boolean;

    @observable
    resetPasswordDialogVisible: boolean;

    @observable
    searchTerm: string = '';

    @observable
    autocompleteSource: SearchUsersAutoCompleteSourceItem[] = [];

    userProfileStore: UserProfileStore;

    @observable
    currentUserId: string;

    @observable
    userGroups: Group[];

    @observable
    userSearchString: string = '';

    @observable
    tableIsLoading: boolean = false;

    @observable
    isUserRoleEditDialogVisible: boolean = false;

    @observable
    customRoles: CustomRole[] = [];

    @observable
    loadingCustomRoles: boolean = false;

    @observable
    customRoleDialogVisible: boolean = false;

    @observable
    customRoleDialogModel: CustomRole | undefined = undefined;

    @observable
    isLoadingUsersCount: boolean = false;

    @observable
    bulkUsersDialogVisible: boolean = false;

    @observable
    bulkUsersDialogMode: 'import' | 'export' = 'import';

    @observable
    bulkUsersProjects: ProjectResult[] = [];

    @observable
    bulkUserBatches: BulkUserBatch[] = [];

    roleFilter: string = '';

    private searchForAutocompleteCallServer: (s: string) => Promise<void>;

    @computed
    get canDeleteUser() {
        return (
            !!this.selectedUser && this.selectedUser.userName !== 'admin' && this.selectedUser.id !== this.currentUserId
        );
    }

    @computed
    get userGroupSelectValues() {
        const emptyGroup: Group = { users: [], usersGroup: { id: '', name: 'Ungrouped users', path: '' } };
        return this.userGroups ? [emptyGroup, ...this.userGroups] : [emptyGroup];
    }

    @computed
    get filteredUsers() {
        if (this.userSearchString.trim() !== '') {
            const searchStringLower = this.userSearchString.toLowerCase();
            return this.users.filter(
                u =>
                    (u.firstName && u.firstName.toLowerCase().includes(searchStringLower)) ||
                    (u.lastName && u.lastName.toLowerCase().includes(searchStringLower)) ||
                    (u.email && u.email.toLowerCase().includes(searchStringLower)) ||
                    (u.roles &&
                        u.roles
                            .map(r => r.name)
                            .join(', ')
                            .toLowerCase()
                            .includes(searchStringLower)) ||
                    (u.groups &&
                        u.groups
                            .map(g => this.getGroupNameById(g))
                            .join(', ')
                            .toLowerCase()
                            .includes(searchStringLower))
            );
        }

        return this.users;
    }

    @computed
    get sortedRoles() {
        const sortedRoleNames =
            this.roles && Object.keys(permissions).filter(y => this!.roles.find(x => x.name === y)!);
        return sortedRoleNames ? sortedRoleNames.map(x => this.roles.find(y => y.name === x)!) : [];
    }

    @computed
    get rolesWithLabels() {
        if (!this.roles) {
            return [];
        }

        return this.roles.map(role => ({
            value: role.name,
            label: Utils.getAppRoleDisplayName(role.name)
        }));
    }

    @computed
    get usersWithGroupRoles(): UserModel[] {
        if (!this.groupRoleMappings || this.groupRoleMappings.length === 0) {
            return this.users;
        }
        const res = this.users.map(user => {
            let newRoles = [...user.roles];
            const allGroupMappings = this.groupRoleMappings.filter(grm => user.groups.includes(grm.groupId));
            const allGroupRoles = _.flatMap(allGroupMappings, group => group.roles);
            newRoles = _.uniqBy([...newRoles, ...allGroupRoles], 'id');
            return { ...user, roles: newRoles };
        });
        return res;
    }

    constructor(
        private service: GlobalAdministrationService,
        private errorStore: ErrorStore,
        private readonly usersStore: UsersStore,
        private readonly projectService: ProjectsService
    ) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        security.inst.loadUserInfo().then((r: any) => {
            this.currentUserId = r.sub;
        });
    }

    @action.bound
    setUsersCurrentPage(page: number) {
        this.usersCurrentPage = page;
    }

    @action.bound
    setUsersTotalCount(count: number) {
        this.usersTotalCount = count;
    }

    @action.bound
    setUserSearchString(searchString: string) {
        this.userSearchString = searchString;
    }

    @action.bound
    setRoleFilter(roleFilter: string) {
        this.roleFilter = roleFilter;
    }

    @action.bound
    async performSearch(searchString: string) {
        if (searchString) {
            this.setRoleFilter('');
        }
        this.userSearchString = searchString;
        this.users = [];
        this.setUsersCurrentPage(1);
        this.getUsersCount();
        await this.getUsers();
    }

    @action.bound
    setCustomRoleDialogVisible(visible: boolean) {
        this.customRoleDialogVisible = visible;
    }

    @action.bound
    setCustomRoleDialogModel(model: CustomRole | undefined) {
        this.customRoleDialogModel = model;
    }

    @action.bound
    setSelectedProjectId(selectedProjectId: string | null) {
        this.selectedProjectId = selectedProjectId;
    }

    @action.bound
    setIsUserRoleEditDialogVisible(isVisible: boolean) {
        this.isUserRoleEditDialogVisible = isVisible;

        if (!isVisible) {
            this.setSelectedProjectId(null);
        }
    }

    @action.bound
    setLoadingCustomRoles(loading: boolean) {
        this.loadingCustomRoles = loading;
    }

    @action.bound
    async getCustomRoles() {
        try {
            this.setLoadingCustomRoles(true);
            var roles = await this.service.getCustomRoles();
            runInAction(() => {
                this.customRoles = roles;
            });
        } catch (ex) {
            console.error(ex);
        } finally {
            this.setLoadingCustomRoles(false);
        }
    }

    @action.bound
    setIsLoadingUsersCount(isLoading: boolean) {
        this.isLoadingUsersCount = isLoading;
    }

    @action.bound
    setBulkUsersDialogVisible(visible: boolean, mode: 'import' | 'export' = 'import') {
        this.bulkUsersDialogVisible = visible;
        this.bulkUsersDialogMode = mode;
    }

    @action.bound
    async loadBulkUsersLookups() {
        const [projects, batches] = await Promise.all([
            this.projectService.getProjects(),
            this.service.getBulkUserBatches()
        ]);
        runInAction(() => {
            this.bulkUsersProjects = projects;
            this.bulkUserBatches = batches;
        });
    }

    async downloadBulkUsersTemplate() {
        const blob = await this.service.downloadBulkUsersTemplate();
        if (blob) {
            Utils.downloadBlob(blob, 'bulk-user-template.xlsx');
        }
    }

    async validateBulkUsersFile(file: File): Promise<BulkUserValidationResult | undefined> {
        const resp = await this.service.validateBulkUsersFile(file);
        if (resp.status !== 200) {
            message.error(resp.data?.title || resp.data || 'Could not validate user list.');
            return undefined;
        }

        return resp.data as BulkUserValidationResult;
    }

    async validateBulkUsersRows(rows: BulkUserRow[]) {
        return this.service.validateBulkUsersRows(rows);
    }

    async importBulkUsers(request: BulkUserImportRequest): Promise<BulkUserImportResponse | undefined> {
        const resp = await this.service.importBulkUsers(request);
        if (resp.status !== 200) {
            message.error(resp.data?.title || resp.data || 'Could not import users.');
            return undefined;
        }

        await this.handlePageChange(this.usersCurrentPage);
        await this.usersStore.getUsers();
        await this.loadBulkUsersLookups();
        message.success('Users imported successfully.');
        return resp.data as BulkUserImportResponse;
    }

    async exportBulkUsers(mode: 'all' | 'batch', batchId?: string) {
        const blob = await this.service.exportBulkUsers(mode, batchId);
        if (blob) {
            Utils.downloadBlob(blob, `bulk-user-credentials-${new Date().toISOString().slice(0, 10)}.xlsx`);
        }
    }

    downloadBulkCredentials(response: BulkUserImportResponse) {
        const byteCharacters = window.atob(response.credentialsSheetBase64);
        const byteNumbers = new Array(byteCharacters.length);
        for (let i = 0; i < byteCharacters.length; i++) {
            byteNumbers[i] = byteCharacters.charCodeAt(i);
        }

        Utils.downloadBlob(
            new Blob([new Uint8Array(byteNumbers)], {
                type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
            }),
            response.fileName
        );
    }

    @action.bound
    async getUsersCount() {
        try {
            this.setIsLoadingUsersCount(true);
            this.setUsersTotalCount(await this.service.getUserCount(this.userSearchString));
        } catch (err) {
            console.error(err);
        } finally {
            this.setIsLoadingUsersCount(false);
        }
    }

    @action.bound
    async saveCustomRole(name: string, rolePermissions: string[], id?: string) {
        try {
            let resp: ResponseData;
            if (id) {
                resp = await this.service.updateCustomRole(id, name, rolePermissions);
            } else {
                resp = await this.service.createCustomRole(name, rolePermissions);
            }

            if (resp.status === 200) {
                this.setCustomRoleDialogVisible(false);
                this.setCustomRoleDialogModel(undefined);
                const msg = `Role ${id ? 'updated' : 'created'} successfully`;
                message.success(msg);
                await this.getCustomRoles();
            } else {
                console.log(resp.status, resp.statusText, resp.data);
                const msg = `Error during role ${id ? 'update' : 'creation'}`;
                message.error(msg);
            }
        } catch (ex) {
            console.error(ex);
        }
    }

    @action.bound
    async deleteCustomRole(roleId: string) {
        try {
            const resp = await this.service.deleteCustomRole(roleId);

            if (resp.status === 200) {
                runInAction(() => {
                    this.customRoles = this.customRoles.filter(r => r.id !== roleId);
                });
                const msg = 'Role deleted successfully';
                message.success(msg);
            } else {
                console.log(resp.status, resp.statusText, resp.data);
                const msg = 'Error during role deletion';
                message.error(msg);
            }
        } catch (ex) {
            console.error(ex);
        }
    }

    @action.bound
    async handlePageChange(page: number) {
        this.setUsersCurrentPage(page);
        this.users = [];
        await this.getUsers();
    }

    @action
    async getUsers() {
        this.isLoading = true;
        const offset = this.usersPageSize * (this.usersCurrentPage - 1);
        const users = await this.service.getAppUsersPage(
            offset,
            this.usersPageSize,
            this.userSearchString,
            this.roleFilter
        );
        this.users = users;
        if (users.length > 0) {
            this.selectedUser = users[0];
        }

        this.showLoadMoreFooter = users.length === this.usersPageSize;

        this.isLoading = false;
    }

    @action
    async getGroupRoleMappings() {
        this.isLoading = true;
        this.groupRoleMappings = await this.service.getGroupsRoleMappings();
        this.isLoading = false;
    }

    @action.bound
    async getGroups() {
        this.userGroups = await this.service.getGroups();
    }

    @action.bound
    getGroupNameById(id: string) {
        return this.userGroups?.find(g => g.usersGroup.id === id)?.usersGroup.name || '';
    }

    @action.bound
    getGroupRoleMapping(groupId: string, roleName: string) {
        return this.groupRoleMappings?.find(
            grm => grm.groupId === groupId && grm.roles.some(role => role.name === roleName)
        );
    }

    @action.bound
    selectedUserHasGroup(groupId: string): boolean {
        return this.selectedUser?.groups?.includes(groupId) || false;
    }

    @action
    async init() {
        this.isLoading = true;
        try {
            const [roles, groups, groupRoleMappings] = await Promise.all([
                this.service.getRoles(),
                this.service.getGroups(),
                this.service.getGroupsRoleMappings()
            ]);
            runInAction(() => {
                this.roles = roles;
                this.userGroups = groups;
                this.groupRoleMappings = groupRoleMappings;
            });
        } finally {
            runInAction(() => {
                this.isLoading = false;
            });
        }
    }

    @action
    async getRoles() {
        const roles = await this.service.getRoles();
        this.roles = roles;
    }

    @action
    setIsAddUserDialogVisible(isVisible: boolean) {
        this.isAddUserDialogVisible = isVisible;
    }

    @action
    setIsEditUserDialogVisible(isVisible: boolean) {
        this.isEditUserDialogVisible = isVisible;
    }

    @action
    setResetPasswordDialogVisible(isVisible: boolean) {
        this.resetPasswordDialogVisible = isVisible;
    }

    @action.bound
    async handleNodeSelection(selectedKey: string | null) {
        if (!selectedKey) {
            return;
        }
        this.selectedUser = this.users.find(u => u.id === selectedKey)!;
    }

    @action.bound
    searchForAutocomplete(term: string) {
        this.searchTerm = term;
        if (this.searchTerm) {
            return this.searchForAutocompleteCallServer(this.searchTerm);
        }
        return;
    }

    @action.bound
    async handleUserSelect(userId: string) {
        console.log(userId);
    }

    @action
    async createUpdateUser(userData: UserModel, isUpdate?: boolean) {
        if (isUpdate) {
            this.tableIsLoading = true;
            userData.id = this.selectedUser!.id!;
        }
        let resp = null;
        if (userData.isDomainUser) {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            resp = await this.service.updateDomainUser(userData.userName, userData.roles as any, userData.group);
        } else {
            resp = await this.service.createUpdateUser(userData);
        }
        if (resp.status === 409) {
            const res = resp.data;
            message.error(res.title);
            if (isUpdate) {
                this.setIsEditUserDialogVisible(true);
            }
            return;
        } else if (resp.status !== 200) {
            this.tableIsLoading = false;
        } else {
            const isActionSuccessfull = resp.data;
            if (!isActionSuccessfull) {
                message.error('There were problems with your request, please report to administrator');
                return;
            }
            if (isUpdate) {
                this.handlePageChange(this.usersCurrentPage);
                message.success('User has been updated');
            } else {
                await this.getUsers();
                message.success('New user has been created');
            }
            this.selectedUser = userData.isDomainUser
                ? this.users.find(u => u.id === userData.userName)!
                : this.users.find(u => u.userName === userData.userName)!;
        }

        this.tableIsLoading = false;
        this.setIsAddUserDialogVisible(false);
        this.usersStore.getUsers();
    }

    @action.bound
    async deleteUser() {
        this.isLoading = true;
        const resp = await this.service.deleteUser(this.selectedUser!.id!, this.selectedUser!.isDomainUser);
        if (resp.status === 200) {
            const isActionSuccessfull = resp.data;
            if (!isActionSuccessfull) {
                message.error('There were problems deleting user, please report to administrator');
                return;
            }
            this.setIsEditUserDialogVisible(false);
            this.handlePageChange(this.usersCurrentPage);
            this.selectedUser = null;
            message.success('User has been deleted');
            this.usersStore.getUsers();
        }
        this.isLoading = false;
    }

    async resetPassword(password: string) {
        const resp = await this.service.resetPassword(password, this.selectedUser!.id!);
        if (resp.status === 200) {
            const isPasswordReset = await resp.data;
            if (isPasswordReset) {
                message.success('Password has been successfully reset');
            } else {
                message.error('There were problems resetting your password, please report to administrator');
            }
        }
        this.setResetPasswordDialogVisible(false);
    }

    @action.bound
    async changePassword(newPassword: string, oldPassword: string) {
        try {
            const resp = await this.service.changePassword(newPassword, oldPassword);
            if (resp.status === 200) {
                this.setResetPasswordDialogVisible(false);
                message.success('Password has been successfully reset.');
            } else if (resp.status === 401) {
                message.error('Old password is incorrect.');
            } else {
                message.error('There were problems resetting your password, please report to administrator');
            }
        } catch (err) {
            this.errorStore.addBasicError(err);
        }
    }

    @action.bound
    async updateObjectRoleAssignments(userId: string, objectId: string, objectType: string, roles: string[]) {
        try {
            const resp = await this.service.changeObjectRoleAssignments(userId, objectId, objectType, roles);
            if (resp.status === 200) {
                this.setIsUserRoleEditDialogVisible(false);
                message.success('Roles have been successfully updated.');
            } else {
                message.error('There were problems during role update, please report to administrator');
            }
        } catch (err) {
            this.errorStore.addBasicError(err);
        }
    }
}
